import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sprout, Beaker, ArrowRight, CheckCircle2, Plus, Trash2, CalendarClock, AlertTriangle, Settings2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const today = () => new Date().toISOString().slice(0, 10);

/**
 * One-time onboarding wizard. Auto-opens for an Owner (or super-admin
 * impersonating) when the farm's `onboarded` flag is false.
 *
 * Steps:
 *  1. Rooms            — how many + name them
 *  2. Growing cycle    — review / edit the growing phase template
 *  3. Composting cycle — review / edit the composting phase template
 *  4. Running rooms    — mark running rooms + their spawning date
 *  5. Composting       — batches in progress + their start date
 *  6. Scheduling       — prewetting gap + system staggering suggestion
 *  7. Done
 */
const STEP_LABELS = ['Rooms', 'Growing', 'Composting', 'Running', 'Batches', 'Schedule'];

const SetupWizard = ({ user, open, onClose }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [farmName, setFarmName] = useState(user?.farm_name || 'your farm');

  // Step 1
  const [roomCount, setRoomCount] = useState(16);
  const [rooms, setRooms] = useState([]); // [{room_number, name, active, batch_name, spawning_date}]
  const [roomIdByNumber, setRoomIdByNumber] = useState({});

  // Step 2 — growing template
  const [phases, setPhases] = useState([]); // [{name, duration_days, action_required, harvest}]

  // Step 3 — composting template
  const [compostPhases, setCompostPhases] = useState([]); // [{name, duration_days, action_required}]

  // Step 4 — compost
  const [compostBatches, setCompostBatches] = useState([]); // [{batch_name, start_date, straw_kg}]

  // Step 5 — scheduling
  const [sched, setSched] = useState(null); // {num_rooms, growing_days, composting_days, recommended_spawn_gap_days}
  const [prewettingGap, setPrewettingGap] = useState(3);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [statusRes, roomsRes, tplRes, ctplRes] = await Promise.all([
          axios.get(`${API}/setup/status`, { headers: auth() }),
          axios.get(`${API}/rooms`, { headers: auth() }),
          axios.get(`${API}/cycle-templates/growing`, { headers: auth() }),
          axios.get(`${API}/cycle-templates/composting`, { headers: auth() }),
        ]);
        setFarmName(statusRes.data.farm_name || 'your farm');
        const currentRooms = (roomsRes.data || []).sort((a, b) => (a.room_number || 0) - (b.room_number || 0));
        const map = {};
        currentRooms.forEach((r) => { map[r.room_number] = r.id; });
        setRoomIdByNumber(map);
        setRoomCount(currentRooms.length || 16);
        setRooms(
          currentRooms.map((r) => ({
            room_number: r.room_number, name: r.name, bags: r.bags ?? '', active: false, batch_name: '', spawning_date: today(),
          }))
        );
        setPhases((tplRes.data?.phases || []).map((p) => ({
          name: p.name, duration_days: p.duration_days, action_required: !!p.action_required, harvest: !!p.harvest,
        })));
        setCompostPhases((ctplRes.data?.phases || []).map((p) => ({
          name: p.name, duration_days: p.duration_days, action_required: !!p.action_required,
        })));
      } catch (e) {
        // best effort
      }
    })();
  }, [open]);

  const goNext = () => setStep((s) => s + 1);
  const goPrev = () => setStep((s) => Math.max(1, s - 1));
  const growingTotal = phases.reduce((s, p) => s + (parseInt(p.duration_days, 10) || 0), 0);
  const compostTotal = compostPhases.reduce((s, p) => s + (parseInt(p.duration_days, 10) || 0), 0);

  // ---- Step 1: build/resize the room list, then persist the layout
  const submitRoomLayout = async () => {
    const n = parseInt(roomCount, 10);
    if (!n || n < 1 || n > 200) return toast.error('Pick a number between 1 and 200');
    // resize the rooms array to n while keeping any names entered
    const sized = [];
    for (let i = 1; i <= n; i++) {
      const prev = rooms.find((r) => r.room_number === i);
      sized.push(prev || { room_number: i, name: `Room ${i}`, bags: '', active: false, batch_name: '', spawning_date: today() });
    }
    setRooms(sized);
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/setup/configure-rooms`,
        { rooms: sized.map((r) => ({ room_number: r.room_number, name: r.name || `Room ${r.room_number}`, bags: r.bags === '' ? null : parseInt(r.bags, 10) })) },
        { headers: auth() },
      );
      const roomsRes = await axios.get(`${API}/rooms`, { headers: auth() });
      const map = {};
      (roomsRes.data || []).forEach((r) => { map[r.room_number] = r.id; });
      setRoomIdByNumber(map);
      toast.success(`${n} rooms saved`);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rooms');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 2: growing template editor
  const updatePhase = (idx, key, value) => {
    const next = [...phases];
    next[idx] = { ...next[idx], [key]: value };
    setPhases(next);
  };
  const addPhase = () => setPhases([...phases, { name: '', duration_days: 1, action_required: true, harvest: false }]);
  const removePhase = (idx) => setPhases(phases.filter((_, i) => i !== idx));

  const submitTemplate = async () => {
    const clean = phases
      .map((p) => ({
        name: (p.name || '').trim(),
        duration_days: parseInt(p.duration_days, 10) || 1,
        action_required: !!p.action_required,
        harvest: !!p.harvest,
      }))
      .filter((p) => p.name);
    if (clean.length === 0) return toast.error('Add at least one phase');
    setSubmitting(true);
    try {
      await axios.put(`${API}/cycle-templates/growing`, { phases: clean }, { headers: auth() });
      toast.success(`Growing cycle saved (${clean.reduce((s, p) => s + p.duration_days, 0)} days)`);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save cycle');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 3: composting template editor
  const updateCompostPhase = (idx, key, value) => {
    const next = [...compostPhases];
    next[idx] = { ...next[idx], [key]: value };
    setCompostPhases(next);
  };
  const addCompostPhase = () => setCompostPhases([...compostPhases, { name: '', duration_days: 1, action_required: true }]);
  const removeCompostPhase = (idx) => setCompostPhases(compostPhases.filter((_, i) => i !== idx));

  const submitCompostTemplate = async () => {
    const clean = compostPhases
      .map((p) => ({
        name: (p.name || '').trim(),
        duration_days: parseInt(p.duration_days, 10) || 1,
        action_required: !!p.action_required,
        harvest: false,
      }))
      .filter((p) => p.name);
    if (clean.length === 0) return toast.error('Add at least one phase');
    setSubmitting(true);
    try {
      await axios.put(`${API}/cycle-templates/composting`, { phases: clean }, { headers: auth() });
      toast.success(`Composting cycle saved (${clean.reduce((s, p) => s + p.duration_days, 0)} days)`);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save composting cycle');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 4: seed running growing rooms from their spawning date
  const submitGrowingRooms = async () => {
    setSubmitting(true);
    try {
      let seeded = 0;
      for (const r of rooms.filter((x) => x.active)) {
        const room_id = roomIdByNumber[r.room_number];
        if (!room_id) continue;
        if (!r.batch_name.trim()) {
          toast.error(`Room ${r.room_number} is marked running — batch name required`);
          setSubmitting(false);
          return;
        }
        try {
          await axios.post(
            `${API}/setup/seed-room-cycle`,
            { room_id, batch_name: r.batch_name.trim(), spawning_date: r.spawning_date },
            { headers: auth() },
          );
          seeded += 1;
        } catch (err) {
          toast.error(`Room ${r.room_number}: ${err.response?.data?.detail || 'failed'}`);
        }
      }
      toast.success(`Seeded ${seeded} running room(s)`);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 4: compost
  const addCompostRow = () => setCompostBatches([...compostBatches, { batch_name: '', start_date: today(), straw_kg: '' }]);
  const updateCompostRow = (idx, key, value) => {
    const next = [...compostBatches];
    next[idx] = { ...next[idx], [key]: value };
    setCompostBatches(next);
  };
  const removeCompostRow = (idx) => setCompostBatches(compostBatches.filter((_, i) => i !== idx));

  const submitCompost = async () => {
    setSubmitting(true);
    try {
      let seeded = 0;
      let attempted = 0;
      for (const b of compostBatches) {
        if (!b.batch_name.trim()) continue;
        attempted += 1;
        try {
          await axios.post(
            `${API}/setup/seed-compost-batch`,
            { batch_name: b.batch_name.trim(), start_date: b.start_date, straw_kg: b.straw_kg ? parseFloat(b.straw_kg) : null },
            { headers: auth() },
          );
          seeded += 1;
        } catch (err) {
          toast.error(`Compost '${b.batch_name}': ${err.response?.data?.detail || 'failed'}`);
        }
      }
      if (attempted > 0 && seeded === 0) {
        toast.error('Fix the compost batch errors above, or clear the rows to skip this step.');
        setSubmitting(false);
        return;
      }
      if (seeded) toast.success(`Seeded ${seeded} compost batch(es)`);
      // load scheduling suggestion for the final step
      try {
        const res = await axios.get(`${API}/setup/scheduling-suggestion`, { headers: auth() });
        setSched(res.data);
        if (res.data?.recommended_spawn_gap_days) setPrewettingGap(res.data.recommended_spawn_gap_days);
      } catch (e) { /* ignore */ }
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 5: scheduling warning (computed client-side from the suggestion)
  const schedWarning = () => {
    if (!sched) return null;
    const g = sched.growing_days || 45;
    const n = sched.num_rooms || rooms.length || 1;
    const gap = parseInt(prewettingGap, 10) || 1;
    const roomsNeeded = Math.ceil(g / gap);
    if (roomsNeeded > n) {
      return { type: 'error', text: `A ${gap}-day gap needs ${roomsNeeded} rooms but you have ${n}. Batches will clash — increase the gap to ${sched.recommended_spawn_gap_days} days or add rooms.` };
    }
    if (roomsNeeded < n) {
      return { type: 'warn', text: `A ${gap}-day gap keeps only ${roomsNeeded} of your ${n} rooms busy — some sit idle. You can tighten toward ${sched.recommended_spawn_gap_days} days.` };
    }
    return { type: 'ok', text: `Perfect fit — a ${gap}-day gap keeps all ${n} rooms cycling continuously.` };
  };

  const submitFinish = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/setup/finish`,
        { prewetting_gap_days: parseInt(prewettingGap, 10) || null, spawn_gap_days: sched?.recommended_spawn_gap_days || null },
        { headers: auth() },
      );
      toast.success('Onboarding complete!');
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finish');
    } finally {
      setSubmitting(false);
    }
  };

  const finalClose = () => {
    onClose && onClose();
    window.location.reload();
  };

  const warn = schedWarning();

  return (
    <Dialog open={open} onOpenChange={() => { /* locked — one-time wizard */ }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Welcome to {farmName} — Quick Setup</DialogTitle>
          <DialogDescription>A one-time wizard to bring your farm&apos;s current state into the system.</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        {step <= 6 && (
          <div className="flex items-center gap-1 my-2 text-[11px]">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              return (
                <React.Fragment key={n}>
                  <div className="flex flex-col items-center gap-1">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full font-semibold ${step >= n ? 'bg-[#1A4D2E] text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
                    </div>
                    <span className={step >= n ? 'text-[#1A4D2E]' : 'text-gray-400'}>{label}</span>
                  </div>
                  {n < 6 && <div className={`flex-1 h-0.5 mb-4 ${step > n ? 'bg-[#1A4D2E]' : 'bg-gray-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ----- Step 1: Rooms ----- */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Step 1: Your rooms</h3>
            <p className="text-sm text-gray-600">How many growing rooms does {farmName} have? Name them below.</p>
            <div className="flex items-center gap-3">
              <Label className="text-sm">Number of rooms</Label>
              <Input
                type="number" min={1} max={200} value={roomCount}
                onChange={(e) => {
                  const v = e.target.value; setRoomCount(v);
                  const n = parseInt(v, 10);
                  if (n >= 1 && n <= 200) {
                    const sized = [];
                    for (let i = 1; i <= n; i++) {
                      const prev = rooms.find((r) => r.room_number === i);
                      sized.push(prev || { room_number: i, name: `Room ${i}`, bags: '', active: false, batch_name: '', spawning_date: today() });
                    }
                    setRooms(sized);
                  }
                }}
                className="w-32" data-testid="wiz-room-count"
              />
            </div>
            <p className="text-xs text-gray-500">Enter the <b>number of bags</b> in each room — expected yield is calculated at ~2&nbsp;kg per bag per cycle.</p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[42vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-3 w-10">#</th>
                    <th className="py-2 px-3">Room name</th>
                    <th className="py-2 px-3 w-40">Bags</th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r, idx) => (
                    <tr key={r.room_number} className="border-t border-gray-100">
                      <td className="py-1.5 px-3 font-mono text-gray-400">{r.room_number}</td>
                      <td className="py-1.5 px-3">
                        <Input
                          value={r.name}
                          onChange={(e) => { const next = [...rooms]; next[idx] = { ...r, name: e.target.value }; setRooms(next); }}
                          data-testid={`wiz-room-name-${r.room_number}`}
                        />
                      </td>
                      <td className="py-1.5 px-3">
                        <Input
                          type="number" min={0} placeholder="e.g. 5000" value={r.bags}
                          onChange={(e) => { const next = [...rooms]; next[idx] = { ...r, bags: e.target.value }; setRooms(next); }}
                          data-testid={`wiz-room-bags-${r.room_number}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button onClick={submitRoomLayout} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step1-next">
                {submitting ? 'Saving...' : 'Save & Next'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Step 2: Growing cycle template ----- */}
        {step === 2 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Settings2 className="w-5 h-5 text-[#1A4D2E]" /> Step 2: Growing cycle</h3>
            <p className="text-sm text-gray-600">
              Review the phases of your growing cycle. Adjust the day-count for each phase to match how your farm runs. Total: <b>{growingTotal} days</b>.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[44vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-3">Phase</th>
                    <th className="py-2 px-3 w-24">Days</th>
                    <th className="py-2 px-3 w-20">Action</th>
                    <th className="py-2 px-3 w-20">Harvest</th>
                    <th className="py-2 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {phases.map((p, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="py-1.5 px-3">
                        <Input value={p.name} onChange={(e) => updatePhase(idx, 'name', e.target.value)} data-testid={`wiz-phase-name-${idx}`} />
                      </td>
                      <td className="py-1.5 px-3">
                        <Input type="number" min={1} max={365} value={p.duration_days} onChange={(e) => updatePhase(idx, 'duration_days', e.target.value)} data-testid={`wiz-phase-days-${idx}`} />
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <input type="checkbox" checked={p.action_required} onChange={(e) => updatePhase(idx, 'action_required', e.target.checked)} />
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <input type="checkbox" checked={p.harvest} onChange={(e) => updatePhase(idx, 'harvest', e.target.checked)} />
                      </td>
                      <td className="py-1.5 px-3">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => removePhase(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={addPhase} variant="outline" className="w-full"><Plus className="w-4 h-4 mr-2" /> Add a phase</Button>
            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={goPrev}>Back</Button>
              <Button onClick={submitTemplate} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step2-next">
                {submitting ? 'Saving...' : 'Save & Next'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Step 3: Composting cycle template ----- */}
        {step === 3 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Beaker className="w-5 h-5 text-amber-700" /> Step 3: Composting cycle</h3>
            <p className="text-sm text-gray-600">
              Not every farm composts in 20 days with the same turnings or rest gaps. Adjust the composting phases and their day-counts to match your process. Total: <b>{compostTotal} days</b>.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[44vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-3">Phase</th>
                    <th className="py-2 px-3 w-24">Days</th>
                    <th className="py-2 px-3 w-20">Action</th>
                    <th className="py-2 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {compostPhases.map((p, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="py-1.5 px-3">
                        <Input value={p.name} onChange={(e) => updateCompostPhase(idx, 'name', e.target.value)} data-testid={`wiz-cphase-name-${idx}`} />
                      </td>
                      <td className="py-1.5 px-3">
                        <Input type="number" min={1} max={365} value={p.duration_days} onChange={(e) => updateCompostPhase(idx, 'duration_days', e.target.value)} data-testid={`wiz-cphase-days-${idx}`} />
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <input type="checkbox" checked={p.action_required} onChange={(e) => updateCompostPhase(idx, 'action_required', e.target.checked)} />
                      </td>
                      <td className="py-1.5 px-3">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeCompostPhase(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={addCompostPhase} variant="outline" className="w-full"><Plus className="w-4 h-4 mr-2" /> Add a phase</Button>
            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={goPrev}>Back</Button>
              <Button onClick={submitCompostTemplate} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step3-next">
                {submitting ? 'Saving...' : 'Save & Next'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Step 4: Growing rooms running state ----- */}
        {step === 4 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Sprout className="w-5 h-5 text-[#1A4D2E]" /> Step 4: Running rooms</h3>
            <p className="text-sm text-gray-600">
              Tick rooms that already have a batch growing, and enter the <b>spawning date</b> (the day spawn was added). We back-date the cycle and auto-complete any past phases so nothing shows as overdue.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[46vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-3">#</th>
                    <th className="py-2 px-3">Room</th>
                    <th className="py-2 px-3">Running?</th>
                    <th className="py-2 px-3">Batch name</th>
                    <th className="py-2 px-3">Spawning date</th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r, idx) => (
                    <tr key={r.room_number} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-mono text-gray-400">{r.room_number}</td>
                      <td className="py-2 px-3">{r.name}</td>
                      <td className="py-2 px-3">
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={r.active} onChange={(e) => { const next = [...rooms]; next[idx] = { ...r, active: e.target.checked }; setRooms(next); }} data-testid={`wiz-room-active-${r.room_number}`} />
                          <span className="text-xs">running</span>
                        </label>
                      </td>
                      <td className="py-2 px-3">
                        <Input value={r.batch_name} disabled={!r.active} placeholder={r.active ? 'e.g. Batch-23' : 'idle'}
                          onChange={(e) => { const next = [...rooms]; next[idx] = { ...r, batch_name: e.target.value }; setRooms(next); }}
                          data-testid={`wiz-room-batch-${r.room_number}`} />
                      </td>
                      <td className="py-2 px-3">
                        <Input type="date" value={r.spawning_date} disabled={!r.active} max={today()}
                          onChange={(e) => { const next = [...rooms]; next[idx] = { ...r, spawning_date: e.target.value }; setRooms(next); }}
                          className="w-40" data-testid={`wiz-room-date-${r.room_number}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={goPrev}>Back</Button>
              <Button onClick={submitGrowingRooms} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step4-next">
                {submitting ? 'Saving...' : 'Save & Next'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Step 5: Composting batches in progress ----- */}
        {step === 5 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Beaker className="w-5 h-5 text-amber-700" /> Step 5: Composting in progress</h3>
            <p className="text-sm text-gray-600">Add any compost batches mid-cycle with the date they <b>started</b> (prewetting day). Skip if none.</p>
            <div className="space-y-2">
              {compostBatches.map((b, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 border border-gray-200 rounded-lg">
                  <div className="col-span-4">
                    <Label className="text-xs">Batch name</Label>
                    <Input value={b.batch_name} onChange={(e) => updateCompostRow(idx, 'batch_name', e.target.value)} placeholder="e.g. C-2024-12" data-testid={`wiz-compost-name-${idx}`} />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Start date</Label>
                    <Input type="date" max={today()} value={b.start_date} onChange={(e) => updateCompostRow(idx, 'start_date', e.target.value)} data-testid={`wiz-compost-date-${idx}`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Straw (kg)</Label>
                    <Input type="number" min={0} value={b.straw_kg} onChange={(e) => updateCompostRow(idx, 'straw_kg', e.target.value)} placeholder="optional" />
                  </div>
                  <div className="col-span-1 pt-5">
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeCompostRow(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={addCompostRow} variant="outline" className="w-full" data-testid="wiz-add-compost"><Plus className="w-4 h-4 mr-2" /> Add a compost batch</Button>
            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={goPrev}>Back</Button>
              <Button onClick={submitCompost} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step5-next">
                {submitting ? 'Saving...' : 'Save & Next'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Step 6: Scheduling / prewetting gap ----- */}
        {step === 6 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#1A4D2E]" /> Step 6: Batch scheduling</h3>
            <p className="text-sm text-gray-600">
              How many days do you wait between starting one batch and the next (the prewetting gap)?
              We use your room count and cycle lengths to suggest a gap that keeps every room cycling without clashes.
            </p>

            {sched && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[
                  ['Rooms', sched.num_rooms],
                  ['Growing days', sched.growing_days],
                  ['Composting days', sched.composting_days],
                  ['Full pipeline', `${sched.total_pipeline_days} d`],
                ].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-lg py-2">
                    <div className="text-xl font-bold text-[#1A4D2E]">{v}</div>
                    <div className="text-[11px] text-gray-500">{k}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-[#1A4D2E]/5 border border-[#1A4D2E]/20 rounded-lg p-3 text-sm">
              <b>Recommended gap: {sched?.recommended_spawn_gap_days ?? '—'} days</b> between batches.
              {sched && (
                <span className="text-gray-600"> With {sched.num_rooms} rooms and a {sched.growing_days}-day cycle, spawning a new batch every {sched.recommended_spawn_gap_days} days keeps all rooms busy. Start composting {sched.composting_days} days before each spawn.</span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Label className="text-sm">Your prewetting gap (days)</Label>
              <Input type="number" min={1} max={30} value={prewettingGap} onChange={(e) => setPrewettingGap(e.target.value)} className="w-28" data-testid="wiz-prewetting-gap" />
            </div>

            {warn && (
              <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${warn.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : warn.type === 'warn' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="wiz-sched-warning">
                {warn.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                <span>{warn.text}</span>
              </div>
            )}

            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={goPrev}>Back</Button>
              <Button onClick={submitFinish} disabled={submitting} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-step6-finish">
                {submitting ? 'Saving...' : 'Finish'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ----- Done ----- */}
        {step === 7 && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-3" />
            <h3 className="font-bold text-2xl text-gray-900 mb-1">All set!</h3>
            <p className="text-sm text-gray-600 mb-1">{farmName} is ready. Your dashboards now reflect the seeded rooms and batches.</p>
            <p className="text-xs text-gray-500 mb-4 flex items-center justify-center gap-1"><Sprout className="w-3 h-3" /> Manage rooms anytime from the &quot;Rooms&quot; tab.</p>
            <Button onClick={finalClose} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="wiz-close">Go to dashboard</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SetupWizard;
